// apps/frontend/src/components/missions/missionCatalog.ts
// Source de verite : CATEGORY_STRUCTURE de @mode-app/shared.
// On derive les options du wizard mission depuis la vraie taxonomie
// pour rester en phase avec ce que les vendeurs publient et ce que le
// matcher cherche cote backend. Plus de liste hardcodee qui derive de
// la BDD reelle.
//
// Structure shared : CATEGORY_STRUCTURE[profile].subcategories[mainCat][group] = items[]
// Exemple : CATEGORY_STRUCTURE.ADULT_WOMAN.subcategories.clothing.pantalons
//          = ['jean-slim', 'jean-skinny', 'jean-droit', ...]
//
// On expose dans le wizard :
// - "category" (cle wizard) = nom du groupe (pulls, pantalons, robes, …)
// - "subStyle" = un item precis du groupe (jean-slim, robe-courte, …)

import { CATEGORY_STRUCTURE, type ProductProfileType } from '@mode-app/shared';
import { formatCategoryLabel } from '../selling/categoryLabels';

export interface CategoryDef {
  key: string; // ex: "pantalons"
  label: string; // ex: "Pantalons"
  subStyles: { key: string; label: string }[]; // items du groupe
}

// Quelques regroupements humains ("Hauts", "Pantalons", "Chaussures"…)
// par defaut. On supplemente avec les groupes de la profile concernee
// pour ne rien rater.
const GROUP_LABELS: Record<string, string> = {
  hauts: 'Hauts',
  pulls: 'Pulls / sweats',
  robes: 'Robes',
  jupes: 'Jupes',
  pantalons: 'Pantalons',
  shorts: 'Shorts',
  vestes: 'Vestes',
  manteaux: 'Manteaux',
  pyjamas: 'Pyjamas / nuit',
  'maillot-bain': 'Maillot de bain',
  baskets: 'Baskets',
  chaussures: 'Chaussures',
  bottes: 'Bottes',
  sacs: 'Sacs',
  accessoires: 'Accessoires',
  bijoux: 'Bijoux',
  lingerie: 'Lingerie',
  sport: 'Sport',
  ensembles: 'Ensembles',
  combinaisons: 'Combinaisons',
};

// Profil par defaut quand aucun profil famille n'est selectionne.
const DEFAULT_PROFILE: ProductProfileType = 'ADULT_WOMAN';

// Construit le catalogue dynamique a partir du profile (adult/child) choisi.
// Si profile invalide ou aliase (CHILD_UNISEX → CHILD_GIRL), on retombe
// sur ADULT_WOMAN qui contient la taxonomie la plus complete.
export function getMissionCategories(
  profile: ProductProfileType | string | null | undefined
): CategoryDef[] {
  const profileKey = (profile ||
    DEFAULT_PROFILE) as keyof typeof CATEGORY_STRUCTURE;
  let data = CATEGORY_STRUCTURE[profileKey];
  // Profile aliase (subcategories vide) → fallback ADULT_WOMAN
  if (
    !data ||
    !data.subcategories ||
    Object.keys(data.subcategories).length === 0
  ) {
    data = CATEGORY_STRUCTURE[DEFAULT_PROFILE];
  }

  const result: CategoryDef[] = [];

  // On itere sur tous les mainCategories du profile (clothing, shoes, bags…)
  // et on aplatit chaque groupe (pulls, pantalons…) en une CategoryDef.
  for (const mainCat of data.mainCategories) {
    const subs = (data.subcategories as Record<string, unknown>)[mainCat];
    if (!subs || typeof subs !== 'object') continue;
    for (const [groupKey, items] of Object.entries(
      subs as Record<string, unknown>
    )) {
      if (!Array.isArray(items)) continue;
      const itemsTyped = items as string[];
      if (itemsTyped.length === 0) continue;
      result.push({
        key: groupKey,
        label:
          GROUP_LABELS[groupKey] ??
          groupKey.charAt(0).toUpperCase() +
            groupKey.slice(1).replace(/-/g, ' '),
        subStyles: itemsTyped.map(item => ({
          key: item,
          label: formatCategoryLabel(item),
        })),
      });
    }
  }

  // Tri par label pour stabilite UI
  return result.sort((a, b) => a.label.localeCompare(b.label));
}

// Palette couleurs proposee — couvre l'essentiel de la mode sans
// avoir 50 nuances. Compatible avec ce que les vendeurs renseignent
// dans le champ color du produit.
export const MISSION_COLORS = [
  { key: 'noir', label: 'Noir', swatch: '#000000' },
  { key: 'blanc', label: 'Blanc', swatch: '#FFFFFF' },
  { key: 'gris', label: 'Gris', swatch: '#9CA3AF' },
  { key: 'beige', label: 'Beige', swatch: '#D9C8AC' },
  { key: 'marron', label: 'Marron', swatch: '#6B4423' },
  { key: 'bleu', label: 'Bleu', swatch: '#3D5A8B' },
  { key: 'marine', label: 'Marine', swatch: '#1E2A47' },
  { key: 'rouge', label: 'Rouge', swatch: '#A65E3F' },
  { key: 'rose', label: 'Rose', swatch: '#E4B5B0' },
  { key: 'vert', label: 'Vert', swatch: '#7A8B6F' },
  { key: 'kaki', label: 'Kaki', swatch: '#7A8056' },
  { key: 'jaune', label: 'Jaune', swatch: '#D4C26A' },
  { key: 'orange', label: 'Orange', swatch: '#C97B45' },
  { key: 'violet', label: 'Violet', swatch: '#5D4458' },
  { key: 'creme', label: 'Crème', swatch: '#F0E8D5' },
  {
    key: 'multicolore',
    label: 'Multi',
    swatch: 'linear-gradient(135deg,#C49B8C,#7A8B6F,#3D4A5C)',
  },
];
